// src/components/trip/PhotoCard.tsx
import React, { useState, useEffect, useRef, memo } from 'react';
import { Card, View, CheckboxField } from '@aws-amplify/ui-react';
import { Download, ImageIcon, Eye, Share2, Video } from 'lucide-react';
import { loadPhotoUrl, loadPhotoThumbnailUrl } from '../../services/enhancedPhotoUrlService';
import { downloadPhoto } from '../../services/tripPhotoActions';
import ProgressiveImage from './ProgressiveImage';
import './ProgressiveImage.css';

interface PhotoCardProps {
  photo: {
    id: string;
    fileName: string;
    url?: string; // Allow directly passing URL to avoid reloading
    [key: string]: any;
  };
  index: number;
  isSelectionMode: boolean;
  isSelected: boolean;
  userRole: string | null; // Add userRole to props
  onSelect: (photoId: string, event: React.MouseEvent) => void;
  onClick: (photoId: string, index: number) => void;
}

// Memoized version of PhotoCard for better performance in virtualized lists
const PhotoCard: React.FC<PhotoCardProps> = memo(({
  photo,
  index,
  isSelectionMode,
  isSelected,
  userRole,
  onSelect,
  onClick
}) => {
  const [showActions, setShowActions] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Check if user can download/share photos (participants and creators only)
  const canDownload = userRole === 'PARTICIPANT' || userRole === 'CREATOR';

  // Check if file is a video
  const isVideo = photo.contentType?.startsWith('video/') || 
                  /\.(mp4|mov|avi|m4v)$/i.test(photo.fileName);

  // For videos, mark as loaded immediately since we show a placeholder
  useEffect(() => {
    if (isVideo) {
      setImageLoaded(true);
    }
  }, [isVideo]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileCheck);
      
      // On mobile, always show actions for better UX
      if (mobileCheck) {
        setShowActions(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handlePhotoDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Safety check - prevent unauthorized downloads
    if (!canDownload) return;
    
    try {
      if (photo.url) {
        await downloadPhoto(photo.id, photo.url, photo.fileName);
      } else {
        // If URL not directly available, get it first
        const url = await loadPhotoUrl(photo.id);
        await downloadPhoto(photo.id, url, photo.fileName);
      }
    } catch (error) {
      console.error(`Error downloading photo ${photo.id}:`, error);
      alert('Could not download photo. Please try again.');
    }
  };

  // Handle sharing for mobile devices
  const handlePhotoShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Safety check - prevent unauthorized sharing
    if (!canDownload) return;
    
    try {
      // Get the full URL if not already available
      const url = photo.url || await loadPhotoUrl(photo.id);
      
      if (navigator.share) {
        await navigator.share({
          title: photo.fileName || 'Shared Photo',
          text: 'Check out this photo',
          url: url
        });
      } else {
        // Fallback to download if Web Share API is not available
        await downloadPhoto(photo.id, url, photo.fileName);
      }
    } catch (error) {
      console.error(`Error sharing photo ${photo.id}:`, error);
      // If share fails, try download as fallback
      handlePhotoDownload(e);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <Card 
      ref={cardRef}
      padding="0"
      variation={isSelected ? "outlined" : "elevated"}
      className={`photo-card ${isSelectionMode ? 'selection-mode' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={() => onClick(photo.id, index)}
      onMouseEnter={() => !isMobile && setShowActions(true)}
      onMouseLeave={() => !isMobile && setShowActions(false)}
      backgroundColor="#1A1A1A"
      style={{ height: '100%', position: 'relative' }}
    >
      {/* Selection checkbox */}
      {isSelectionMode && (
        <View className="selection-checkbox">
          <CheckboxField
            name={`photo-select-${photo.id}`}
            checked={isSelected}
            onChange={(e) => onSelect(photo.id, e as unknown as React.MouseEvent)}
            onClick={(e) => e.stopPropagation()}
            label=""
          />
        </View>
      )}

      <div className="photo-container" style={{ height: '100%' }}>
        {isVideo ? (
          // For videos, show a placeholder with video icon
          <div 
            style={{ 
              position: 'relative', 
              width: '100%', 
              height: '100%', 
              backgroundColor: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onLoad={handleImageLoad}
          >
            {/* Video icon background */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                borderRadius: '50%',
                width: '64px',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Video size={32} color="#3b82f6" />
            </div>
            
            {/* Video indicator badge */}
            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                left: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 1
              }}
            >
              <Video size={14} color="#fff" />
              <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>VIDEO</span>
            </div>
          </div>
        ) : (
          // For images, use progressive image component
          <ProgressiveImage
            photoId={photo.id}
            alt={photo.fileName}
            width="100%"
            height="100%"
            objectFit="cover"
            className="photo-image"
            useBlurTransition={false} // Don't use blur effect in grid
            onLoad={handleImageLoad}
          />
        )}
        
        {!isSelectionMode && imageLoaded && (
          <>
            {/* Always-visible overlay with subtle gradient for better visibility on mobile */}
            <div className="photo-overlay-gradient"></div>
            
            {/* Action buttons - shown based on showActions state and permissions */}
            <div className={`photo-actions ${showActions ? 'visible' : ''}`}>
              
              {/* Only show download/share for participants and creators */}
              {canDownload && (
                isMobile ? (
                  // Show share button on mobile
                  <button
                    className="photo-action-btn share-btn"
                    onClick={handlePhotoShare}
                    aria-label="Share photo"
                  >
                    <Share2 size={16} />
                  </button>
                ) : (
                  // Show download button on desktop
                  <button
                    className="photo-action-btn download-btn"
                    onClick={handlePhotoDownload}
                    aria-label="Download photo"
                  >
                    <Download size={16} />
                  </button>
                )
              )}
            </div>
            
            {/* Add filename at bottom for better context on mobile */}
            <div className="photo-filename">
              {photo.fileName.length > 20 
                ? photo.fileName.substring(0, 20) + '...' 
                : photo.fileName}
            </div>
          </>
        )}
      </div>

      {/* Show placeholder if image hasn't loaded yet */}
      {!imageLoaded && (
        <View 
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          textAlign="center"
          display="flex"
          backgroundColor="#252525"
        >
          <div className="flex h-full items-center justify-center">
            <ImageIcon size={24} color="#666" />
          </div>
        </View>
      )}
    </Card>
  );
});

PhotoCard.displayName = 'PhotoCard';

export default PhotoCard;